import { useRef, useCallback } from 'react';

interface UseTypedEventHandlerManagerOptions {
  debug?: boolean;
}

export type UseEventHandlerManagerReturn<
  T extends { [K in keyof T]: (...args: any[]) => any }
> = {
  register: <K extends keyof T>(event: K, callback: T[K]) => void;
  once: <K extends keyof T>(event: K, callback: T[K]) => void;
  unregister: <K extends keyof T>(event: K, callback: T[K]) => void;
  trigger: <K extends keyof T>(event: K, ...args: Parameters<T[K]>) => void;
  triggerAll: (...args: any[]) => void;
};

export function useEventHandlerManager<
  T extends { [K in keyof T]: (...args: any[]) => any }
>(
  options: UseTypedEventHandlerManagerOptions = {}
): UseEventHandlerManagerReturn<T> {
  const handlersRef = useRef<Partial<{ [K in keyof T]: T[K][] }>>({});
  const onceHandlersRef = useRef<Partial<{ [K in keyof T]: T[K][] }>>({});

  const log = (...args: any[]) => {
    if (options.debug) {
      console.debug('[TypedEventManager]', ...args);
    }
  };

  const register = useCallback(
    <K extends keyof T>(event: K, callback: T[K]) => {
      handlersRef.current[event] ||= [];
      handlersRef.current[event]!.push(callback);
      log(`Registered "${String(event)}"`);
    },
    []
  );

  const once = useCallback(<K extends keyof T>(event: K, callback: T[K]) => {
    onceHandlersRef.current[event] ||= [];
    onceHandlersRef.current[event]!.push(callback);
    log(`Registered once "${String(event)}"`);
  }, []);

  const unregister = useCallback(
    <K extends keyof T>(event: K, callback: T[K]) => {
      handlersRef.current[event] = (handlersRef.current[event] || []).filter(
        (cb) => cb !== callback
      );
      onceHandlersRef.current[event] = (
        onceHandlersRef.current[event] || []
      ).filter((cb) => cb !== callback);
      log(`Unregistered "${String(event)}"`);
    },
    []
  );

  const trigger = useCallback(
    <K extends keyof T>(event: K, ...args: Parameters<T[K]>) => {
      const regulars = handlersRef.current[event] || [];
      const onces = onceHandlersRef.current[event] || [];

      regulars.forEach((cb) => cb(...args));
      onces.forEach((cb) => cb(...args));

      if (onces.length > 0) {
        onceHandlersRef.current[event] = [];
        log(`Cleared once listeners for "${String(event)}"`);
      }

      log(`Triggered "${String(event)}" with args:`, args);
    },
    []
  );

  const triggerAll = useCallback(
    (...args: any[]) => {
      (Object.keys(handlersRef.current) as (keyof T)[]).forEach((event) => {
        trigger(event, ...(args as never));
      });
    },
    [trigger]
  );

  return {
    register,
    once,
    unregister,
    trigger,
    triggerAll
  };
}
